import React, {
  forwardRef,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ChevronDown, ChevronUp, CloseBig, SpinnerMedium } from '../../icons';
import { useOutsideAlerter } from '../../utils/useOutsideAlerter';
import { TextField } from '../TextField';
import { Paragraph } from '../Typography';

import {
  Container,
  DropDownContainer,
  DropDownItem,
  IconContainer,
  NoResultsContainer,
} from './Select.styles';
import { SelectOption, SelectProps } from './Select.types';

export const Select = forwardRef(
  (
    {
      id,
      selectedOption,
      options,
      label,
      size,
      noResultsText = 'Não há itens que correspondem à pesquisa',
      onChangeOption,
      onChangeSearch,
      isLoading,
      width,
      ...props
    }: SelectProps,
    ref,
  ) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    let inputRef = useRef<HTMLInputElement>(null);
    if (ref) {
      inputRef = ref as MutableRefObject<HTMLInputElement>; // register.ref ??
    }

    useOutsideAlerter(wrapperRef, () => setShowDropDown(false));
    const [showDropDown, setShowDropDown] = useState(false);
    const [searchValue, setSearchValue] = useState<string | null>();
    const [selectedValue, setSelectedValue] = useState<string>(selectedOption?.value);
    const [inputFocused, setInputFocused] = useState(false);
    const [active, setActive] = useState(0);

    const filteredValues = useMemo(() => {
      if (!searchValue) {
        return options;
      }
      return options?.filter(currentValue =>
        currentValue.value.toLowerCase().includes(searchValue.toLowerCase()),
      );
    }, [searchValue, options]);

    const handleBlur = useCallback(() => {
      setShowDropDown(false);
      setInputFocused(state => !state);
    }, []);

    const handleSelect = useCallback(
      (option: SelectOption) => {
        setSelectedValue(option?.value);
        setSearchValue(undefined);
        onChangeOption?.(option);
        handleBlur();
      },
      [handleBlur, onChangeOption],
    );

    useEffect(() => {
      setSelectedValue(selectedOption?.value);
    }, [handleSelect, selectedOption]);

    const value = props.value ?? searchValue ?? selectedValue ?? '';

    useEffect(() => {
      const existOption = options?.find(option => option.value === selectedValue);

      if ((!inputFocused && !existOption) || (!inputFocused && filteredValues.length === 0)) {
        setSelectedValue('');
        setSearchValue(undefined);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputFocused]);

    const handleChangeSearchValue = (e: React.ChangeEvent<HTMLInputElement>) => {
      const search = e.target.value;
      setSearchValue(search);
      if (!search) {
        setSelectedValue(undefined);
      }
      onChangeSearch?.(search);
    };

    const keyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowUp' && active > 0) {
        setActive(active - 1);
      } else if (event.key === 'ArrowDown' && active < filteredValues.length - 1) {
        setActive(active + 1);
      } else if (event.key === 'ArrowDown') {
        setShowDropDown(true);
      } else if (event.key === 'Enter' && filteredValues?.length > 0) {
        filteredValues[active] && handleSelect(filteredValues[active]);
      }
    };

    function IconUp() {
      return (
        <IconContainer
          onClick={() => {
            inputRef && inputRef.current && inputRef.current.blur();
            handleBlur();
          }}
        >
          <ChevronUp />
        </IconContainer>
      );
    }

    function IconDown() {
      return (
        <IconContainer
          onClick={() => {
            setShowDropDown(true);
            inputRef && inputRef.current && inputRef.current.focus();
          }}
        >
          <ChevronDown />
        </IconContainer>
      );
    }

    function IconDelete() {
      return (
        <IconContainer
          onClick={() => {
            handleSelect(undefined);
            inputRef && inputRef.current && inputRef.current.focus();
            inputRef && inputRef.current && inputRef.current.blur();
          }}
        >
          <CloseBig />
        </IconContainer>
      );
    }

    function TrailingIcon() {
      if (isLoading) {
        return <SpinnerMedium />;
      }
      if (selectedValue || selectedOption?.value) {
        return <IconDelete />;
      }
      if (showDropDown) {
        return <IconUp />;
      } else {
        return <IconDown />;
      }
    }

    return (
      <Container ref={wrapperRef} size={size} data-testid={props['data-testid'] || 'tmov-select'}>
        <TextField
          {...props}
          id={id}
          ref={inputRef}
          data-testid={'dropdown-input'}
          value={value}
          label={label}
          trailingIcon={TrailingIcon}
          size={size}
          onKeyDown={keyDownHandler}
          onFocus={() => {
            setInputFocused(true);
            setShowDropDown(true);
          }}
          onBlur={e => {
            setTimeout(async () => {
              handleBlur();
              props.onBlur && props.onBlur(e);
            }, 150);
          }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handleChangeSearchValue(e);
            props.onChange && props.onChange(e);
          }}
          width={width ?? '100%'}
          autoComplete="off"
        />
        <DropDownContainer show={!isLoading && showDropDown} width={width}>
          {filteredValues?.length ? (
            filteredValues.map((currentValue, i) => (
              <DropDownItem
                type="button"
                data-testid={`${id ? id + '-' : ''}${currentValue.id.toString()}`}
                key={`${id ? id + '-' : ''}${currentValue.id.toString()}`}
                onClick={() => handleSelect(currentValue)}
                onMouseMoveCapture={() => setActive(i)}
                active={active === i}
              >
                <Paragraph variant="p2" style={{ margin: 0 }}>
                  {currentValue.value}
                </Paragraph>
              </DropDownItem>
            ))
          ) : (
            <NoResultsContainer data-testid="no-results">
              <Paragraph variant="p2" style={{ margin: 0 }}>
                {noResultsText}
              </Paragraph>
            </NoResultsContainer>
          )}
        </DropDownContainer>
      </Container>
    );
  },
);
Select.displayName = 'Select';
